import React, { useContext, useEffect } from 'react';
import Draggable from 'react-draggable';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { v4 } from 'uuid';

import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Paper, { PaperProps } from '@material-ui/core/Paper';
import Slide from '@material-ui/core/Slide';
import { TransitionProps } from '@material-ui/core/transitions';
import ControlCameraIcon from '@material-ui/icons/ControlCamera';

import { MultimediaAndHintReviewStoreSlice } from '../../helpers/shared-actions.types';
import { isEmpty } from '../../helpers/utils';
import {
  addMultimediaAndHintReview_AC, dismissMultimediaAndHintReview_AC, multimediaOkClickedAC
} from '../../store/actions/executor.actions';
import { fileResourceUrl } from '../../utils/urlUtils';
import MonitorContext from '../S80/MonitorContext';

function PaperComponent(props: PaperProps) {
  return (
    <Draggable
      handle="#alert-dialog-slide-title"
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

const Transition = React.forwardRef(function Transition(
  props: TransitionProps & { children?: React.ReactElement<any, any> },
  ref: React.Ref<unknown>
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialog = styled(Dialog)`
  max-height: 960px !important;
  ::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  .MuiBackdrop-root {
    background-color: rgba(0, 0, 0, 0.3);
  }
  .MuiTypography-colorTextSecondary {
    color: rgba(0, 0, 0, 1);
  }
`;

const multimediaAndHintReviewToMultimediaPayload = (
  storeState: MultimediaAndHintReviewStoreSlice
) => {
  if (storeState.displayIndex === -1) {
    return {};
  }
  const payload = storeState.list[storeState.displayIndex];
  if (payload.alertType === 'hint') {
    return {
      ...payload.actionPayload,
      type: 'hint',
      displayText: payload.actionPayload.hint,
    };
  }
  if (payload.alertType === 'multimedia') {
    return {
      ...payload.actionPayload,
      type: 'multimedia',
    };
  }

  return {
    type: payload.alertType,
    asset: payload.actionPayload.asset,
    xPosition: payload.actionPayload.xPosition,
    yPosition: payload.actionPayload.yPosition,
    duration: payload.actionPayload.duration,
  };
};

export default function MultimediaDialog() {
  const [open, setOpen] = React.useState(false);
  const monitorContext = useContext<any>(MonitorContext);
  const dialogMonitor = useSelector(
    (state: any) => state.executor.preferences.dialogMonitor
  );
  const [messages, setMessages] = React.useState<any>([]);
  const dispatch = useDispatch();
  const executor = useSelector((state: any) => state.executor);
  const resources = useSelector((state: any) => state.resources);
  const token = useSelector((state: any) => state.authenticate.token);
  const isOffline = useSelector(
    (state: any) => state.resources.offlineMode
  );
  const addToRenderMessageQueue = (
    multimediaAsset: any,
    isReviewAsset: boolean
  ) => {
    if (!isEmpty(multimediaAsset)) {
      let imagePayload = {};
      if (
        multimediaAsset.type === 'image' ||
        multimediaAsset.type === 'video'
      ) {
        imagePayload = resources[multimediaAsset.asset];
      }
      setMessages((msgs: any) => [
        ...msgs,
        { ...multimediaAsset, ...imagePayload, id: v4(), isReviewAsset },
      ]);
    } else {
      setMessages([]);
    }
  };
  useEffect(() => {
    addToRenderMessageQueue(executor.multimediaText, false);
  }, [executor.multimediaText]);

  useEffect(() => {
    if (!isEmpty(executor.multimediaText)) {
      // When there is a multimedia text, we don't want to show the multimedia and hint reviews
      return;
    }
    const multimediaAsset = multimediaAndHintReviewToMultimediaPayload(
      executor.multimediaAndHintReviews
    );
    addToRenderMessageQueue(multimediaAsset, true);
  }, [executor.multimediaAndHintReviews]);

  useEffect(() => {
    if (messages.length > 0) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  }, [messages]);

  const handleClose = (isReviewAsset: boolean) => {
    setOpen(false);
    setMessages(messages.slice(1));
    if (!isReviewAsset) {
      dispatch(multimediaOkClickedAC());
    } else {
      dispatch(dismissMultimediaAndHintReview_AC({}));
    }
  };

  const renderDialogContent = (message: any, resources: any, assetId: any, isOffline: boolean) => {
    const { type, displayText, value, filename } = message;
    switch (type) {
      case 'hint':
        return (
          <DialogContentText id="alert-dialog-slide-description">
            {displayText}
          </DialogContentText>
        );
      case 'multimedia':
        return (
          <DialogContentText
            id="alert-dialog-slide-description"
            dangerouslySetInnerHTML={{
              ['__html']: resources[assetId],
            }}
          />
        );
      case 'image':
        const imgSrc = fileResourceUrl(token, value, filename, isOffline);
        return <img src={imgSrc} />;
      case 'video':
        const vidSrc = fileResourceUrl(token, value, filename, isOffline);
        return <video src={vidSrc} controls />;
    }
  };
  const isError = messages.length > 0 && messages[0].isError;
  useEffect(() => {
    console.log('MD MultimediaDialog ', monitorContext.monitor, dialogMonitor);
  }, []);

  const addToReview = (message) => {
    setOpen(false);
    setMessages(messages.slice(1));
    dispatch(
      addMultimediaAndHintReview_AC({
        alertType: message.type,
        actionPayload: {
          asset: message.asset,
          duration: message.duration,
          xPosition: message.xPosition,
          yPosition: message.yPosition,
          displayText: message.displayText,
          assetContent: message.assetContent,
          hint: message.type === 'hint' ? message.displayText : undefined,
        },
      })
    );
    dispatch(multimediaOkClickedAC());
  };

  if (monitorContext.monitor !== dialogMonitor) {
    return <></>;
  }
  return (
    <div>
      <StyledDialog
        open={open}
        maxWidth="md"
        TransitionComponent={Transition}
        PaperComponent={PaperComponent}
        keepMounted
        disableBackdropClick
        onClose={handleClose}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
        style={isError ? { zIndex: 99999 } : {}}
      >
        <DialogTitle
          style={{
            cursor: 'move',
          }}
          id="alert-dialog-slide-title"
        ></DialogTitle>
        <ControlCameraIcon
          style={{
            position: 'absolute',
            right: '5px',
            top: '5px',
            display: 'none',
            // cursor: 'pointer',
          }}
          id="drag-handle"
        />
        <DialogContent>
          {messages.length > 0 &&
            renderDialogContent(messages[0], resources, messages[0].asset, !!isOffline)}
        </DialogContent>
        <DialogActions>
          {messages[0] && !messages[0].noActionButtons && (
            <>
              <Button
                onClick={() => handleClose(messages[0]?.isReviewAsset)}
                color="primary"
              >
                Ok
              </Button>
              {!messages[0]?.isReviewAsset && (
                <Button
                  color="primary"
                  onClick={() => {
                    addToReview(messages[0]);
                  }}
                >
                  Add to review
                </Button>
              )}
            </>
          )}
        </DialogActions>
      </StyledDialog>
    </div>
  );
}
